// -- [Format seconds as human-readable duration: "2 years, 4 months, 19 days, 22:36:04"]

const SECONDS_PER_MINUTE = 60
const SECONDS_PER_HOUR = 3600
const SECONDS_PER_DAY = 86400
const DAYS_PER_MONTH = 365.25 / 12
const DAYS_PER_YEAR = 365.25
const SECONDS_PER_MONTH = SECONDS_PER_DAY * DAYS_PER_MONTH
const SECONDS_PER_YEAR = SECONDS_PER_DAY * DAYS_PER_YEAR

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * Format whole seconds as "X years, Y months, Z days, HH:MM:SS".
 * Years/months/days omitted if zero; time part is always HH:MM:SS (e.g. 22:36:04).
 */
export function formatSecondsToDuration(totalSeconds: number): string {
  let s = Math.max(0, Math.floor(totalSeconds))
  const years = Math.floor(s / SECONDS_PER_YEAR)
  s -= years * SECONDS_PER_YEAR
  const months = Math.floor(s / SECONDS_PER_MONTH)
  s -= months * SECONDS_PER_MONTH
  const days = Math.floor(s / SECONDS_PER_DAY)
  s -= days * SECONDS_PER_DAY
  const hours = Math.floor(s / SECONDS_PER_HOUR)
  s -= hours * SECONDS_PER_HOUR
  const minutes = Math.floor(s / SECONDS_PER_MINUTE)
  const seconds = s % SECONDS_PER_MINUTE

  const timePart = `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`
  const parts: string[] = []
  if (years > 0) parts.push(`${years} ${years === 1 ? 'yr' : 'yrs'}`)
  if (months > 0) parts.push(`${months} ${months === 1 ? 'mo' : 'mo'}`)
  if (days > 0) parts.push(`${days} ${days === 1 ? 'd' : 'd'}`)
  parts.push(timePart)

  return parts.join(', ')
}

/**
 * Format whole seconds as "X hours, Y mins, Zs".
 * Hours are total hours across the full duration (not capped at 24).
 */
export function formatSecondsToHoursMinutesSeconds(totalSeconds: number): string {
  const total = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(total / SECONDS_PER_HOUR)
  const remainderAfterHours = total % SECONDS_PER_HOUR
  const minutes = Math.floor(remainderAfterHours / SECONDS_PER_MINUTE)
  const seconds = remainderAfterHours % SECONDS_PER_MINUTE

  return `${hours.toLocaleString('en-AU')} hours, ${minutes} mins, ${seconds}s`
}

/**
 * Format average search time: ms if < 1s, otherwise seconds with 2 decimal places.
 * processingTimeTotal is in seconds; count is number of searches.
 */
export function formatAverageSearchTime(
  processingTimeTotalSeconds: number,
  count: number
): string {
  if (count <= 0) return '—'
  const avgSeconds = processingTimeTotalSeconds / count
  if (avgSeconds < 1) {
    const ms = Math.round(avgSeconds * 1000)
    return `${ms}ms`
  }
  return `${avgSeconds.toFixed(2)}s`
}
